<template>
  <div class="event-format-tab">
    <div class="row mb-2" v-if="validationErrors.apiError">
      <div class="col">
        <el-alert type="error">
          {{ validationErrors.apiError }}
        </el-alert>
      </div>
    </div>

    <!-- Seating Style Section -->
    <div class="row">
      <div class="col-12">
        <div class="form-section mr-1">
          <base-input
            label="Seating Style"
            >
            <SeatingStyle 
              :modelValue="form.seating_style"
              @update:modelValue="onSeatingStyleChange"
              label=""
              :error="validationErrors.seating_style"
            />
          </base-input>
        </div>
      </div>
    </div>

    <!-- Breakout Room Section -->
    <div class="row mt-4">
      <div class="col-lg-6">
            <base-input
            label="Are any breakout rooms or green rooms required?"
            >
                <el-select v-model="roomRequirement" placeholder="Are any breakout rooms or green rooms required?" class="w-100">
                    <el-option
                    v-for="(option, index) in room_requirement_options"
                    :key="index"
                    :label="option.label"
                    :value="option.value">
                    </el-option>
                </el-select>
            </base-input>
        </div>
        <div class="col-6">
          <div class="form-section">
            <base-input
            label="AV Requirements"
            >
              <textarea
                class="form-control"
                v-model="form.av_requirements"
                :rows="4"
              ></textarea>
            </base-input>
          </div>
        </div>
    </div>

    <!-- Save Button -->
    <div class="row mt-3">
      <div class="col-12 text-right">
        <base-button 
          type="primary" 
          @click="saveEventFormat"
          :loading="loading"
        >
          Save & Next
        </base-button>
      </div>
    </div>
  </div>
</template>

<script>
import {
  Select,
  Option,
  Notification,
  Alert
} from "element-ui";
import { mapState } from "vuex";
import LeadEventService from '@/services/lead-event.service';
import SeatingStyle from '@/components/Custom/SeatingStyle.vue';
import leadEventFormMixin from '@/helpers/leadEventFormMixin';

export default {
  name: 'EditEventStep2',
  mixins: [leadEventFormMixin],
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
    [Notification.name]: Notification,
    SeatingStyle,
    [Alert.name]: Alert
  },
  props: {
    leadEvent: {
      type: Object,
      required: true,
    },
    sessionId: {
      type: [Number, String],
      default: null
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      validationErrors: {},
      fieldErrors: {},
      form: {
        step: 2,
        direct_update: true,
        seating_style: null,
        green_rooms_required: null,
        break_rooms_required: null,
        av_requirements: null,
      },
      room_requirement_options: [
        { label: 'I need a green room', value: 'green_room_required' },
        { label: 'I need a breakout room', value: 'breakout_room_required' },
        { label: 'I need both', value: 'both' },
        { label: 'No', value: null },
      ],
    };
  },
  computed: {
    ...mapState("country/", {
      country: (state) => state.country || 'AU',
    }),
    roomRequirement: {
      get() {
        if (this.form.green_rooms_required && this.form.break_rooms_required) {
          return 'both';
        }
        if (this.form.green_rooms_required) {
          return 'green_room_required';
        }
        if (this.form.break_rooms_required) {
          return 'breakout_room_required';
        }
        return null;
      },
      set(value) {
        if (value === 'both') {
          this.form.green_rooms_required = true;
          this.form.break_rooms_required = true;
        } else if (value === 'green_room_required') {
          this.form.green_rooms_required = true;
          this.form.break_rooms_required = false;
        } else if (value === 'breakout_room_required') {
          this.form.green_rooms_required = false;
          this.form.break_rooms_required = true;
        } else {
          this.form.green_rooms_required = false;
          this.form.break_rooms_required = false;
        }
      }
    },
  },
  methods: {
    async saveEventFormat() {
      
      this.validationErrors = {};
      
      if (!this.form.seating_style) {
        this.$set(this.validationErrors, 'seating_style', 'Seating Style is required.');
        return;
      }

      this.$emit('update:loading', true);
      try {
        var data  = {...this.form};
        data.green_or_breakout_rooms_required = this.roomRequirement; 
        
        if (this.form.seating_style) {
          data.seating_style = [this.form.seating_style];
        }

        try {
            if (this.sessionId) {
              await LeadEventService.updateEventSession(this.leadEvent.id, {
                ...data,
                session_id: this.sessionId,
              });
            } else {
              await LeadEventService.updateCorporateLeadEvent(this.leadEvent.id, data);
            }
        } catch (error) {
            console.error('Error updating event format:', error);
            const responseData = error?.response?.data || {};
            const message = responseData.error?.message
              || responseData.message
              || 'Failed to save event format. Please check your inputs and try again.';

            if (responseData.errors && typeof responseData.errors === 'object') {
              Object.keys(responseData.errors).forEach((field) => {
                const firstError = Array.isArray(responseData.errors[field])
                  ? responseData.errors[field][0]
                  : responseData.errors[field];

                this.$set(this.validationErrors, field, firstError);
              });
            }

            this.$set(this.validationErrors, 'apiError', message);

            Notification.error({
              title: 'Error',
              message,
              type: 'error',
              duration: 5000,
            });

            return; // Exit early on API error
        }

        // Emit success event to parent
        this.$emit('onSave', {
          message: 'Event format saved successfully',
        });
        
        // Show success message
        Notification.success({
            title: 'Success',
            message: 'Event format saved successfully!',
            type: 'success',
        });
        
      } catch (error) {
        console.error('Error saving event format:', error);
        // Show error message
        const responseData = error?.response?.data || {};
        const message = responseData.error?.message
          || responseData.message
          || 'Failed to save event format. Please check your inputs and try again.';

        if (responseData.errors && typeof responseData.errors === 'object') {
          Object.keys(responseData.errors).forEach((field) => {
            const firstError = Array.isArray(responseData.errors[field])
              ? responseData.errors[field][0]
              : responseData.errors[field];

            this.$set(this.validationErrors, field, firstError);
          });
        }

        this.$set(this.validationErrors, 'apiError', message);
        
        Notification.error({
          title: 'Error',
          message,
          type: 'error',
        });
        this.$emit('update:loading', false);
      }
    },
    
    updateFormFromLeadEvent(leadEvent) {
      // Update form fields from leadEvent data, preserving existing values when new data is missing
      const safeUpdate = (currentValue, newValue) => {
        return ! [null, undefined, ''].includes(newValue) ? newValue : currentValue;
      };

      let dataToUse = leadEvent;
      if (this.sessionId) {
        const sessions = leadEvent.event_sessions || leadEvent.eventSessions || [];
        const session = sessions.find(s => s.id === this.sessionId);
        if (session) {
          dataToUse = {
            ...leadEvent, // Fallback to leadEvent for missing fields
            ...session
          };
        }
      }

      // Handle green room value conversion
      let greenRoomRequired = this.safeBooleanUpdate(dataToUse.green_rooms_required);

      let breakRoomValue = this.safeBooleanUpdate(dataToUse.break_rooms_required);

      let seatingStyleValue = dataToUse.seating_style;
      if (Array.isArray(seatingStyleValue) && seatingStyleValue.length > 0) {
        seatingStyleValue = seatingStyleValue[0];
      }

      this.form.seating_style = safeUpdate(this.form.seating_style, seatingStyleValue);
      this.form.green_rooms_required = safeUpdate(this.form.green_rooms_required, greenRoomRequired);
      this.form.break_rooms_required = safeUpdate(this.form.break_rooms_required, breakRoomValue);
      this.form.av_requirements = safeUpdate(this.form.av_requirements, dataToUse.av_requirements);

      console.log('Event format form updated with data:', this.form);
    },
    onSeatingStyleChange(value) {
      this.form.seating_style = value;
      if (this.validationErrors.seating_style) {
        this.$delete(this.validationErrors, 'seating_style');
      }
      console.log('Seating style changed to:', value);
    }
  },
  mounted() {
    // Initialize form with leadEvent data when component is mounted
    if (this.leadEvent) {
      this.updateFormFromLeadEvent(this.leadEvent);
    }
  },
  watch: {
    leadEvent: {
      handler(newLeadEvent) {
        if (newLeadEvent) {
          this.updateFormFromLeadEvent(newLeadEvent);
        }
      },
      deep: true,
      immediate: true
    },
    sessionId: {
      handler(newSessionId) {
        this.updateFormFromLeadEvent(this.leadEvent);
      },
      immediate: false
    }
  }
};
</script>